/**
 * Mindvalley States - Shopify Theme JavaScript
 */

(function() {
  'use strict';

  // ==========================================
  // Utility Functions
  // ==========================================
  
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ==========================================
  // Segment Analytics Tracking
  // ==========================================
  
  const SegmentTracking = {
    /**
     * Format Shopify product data for Segment
     */
    formatProduct(item, source = null) {
      const product = {
        product_id: String(item.product_id || item.id || ''),
        sku: item.sku || item.variant_id || '',
        name: item.product_title || item.title || '',
        price: (item.price || 0) / 100, // Convert cents to dollars
        quantity: item.quantity || 1,
        category: item.product_type || 'Supplements',
        brand: 'Mindvalley States',
        image_url: item.image || item.featured_image || '',
        url: item.url || window.location.origin + '/products/' + (item.handle || '')
      };

      // Add variant info if available
      if (item.variant_title && item.variant_title !== 'Default Title') {
        product.variant = item.variant_title;
      }

      // Add source if provided (e.g., 'quiz', 'cross-sell')
      if (source) {
        product.source = source;
      }

      return product;
    },

    /**
     * Track Product Added to Cart
     */
    trackProductAdded(item, quantity, source = null) {
      if (typeof window.analytics === 'undefined') return;
      
      const product = this.formatProduct(item, source);
      product.quantity = quantity;

            
      window.analytics.track('Product Added', product);
    },

    /**
     * Track Product Removed from Cart
     */
    trackProductRemoved(item, quantity) {
      if (typeof window.analytics === 'undefined') return;
      
      const product = this.formatProduct(item);
      product.quantity = quantity;

            
      window.analytics.track('Product Removed', product);
    },

    /**
     * Track Cart Viewed
     */
    trackCartViewed(cart) {
      if (typeof window.analytics === 'undefined') return;
      
      const products = cart.items.map(item => this.formatProduct(item));
      
      const eventData = {
        cart_id: cart.token || String(Date.now()),
        products: products,
        value: cart.total_price / 100 // Convert cents to dollars
      };

            
      window.analytics.track('Cart Viewed', eventData);
    },

    /**
     * Track Checkout Started
     */
    trackCheckoutStarted(cart) {
      if (typeof window.analytics === 'undefined') return;
      
      const products = cart.items.map(item => this.formatProduct(item));
      
      const eventData = {
        order_id: cart.token || String(Date.now()),
        affiliation: 'Mindvalley States Store',
        value: cart.total_price / 100,
        revenue: cart.total_price / 100,
        currency: 'USD',
        products: products
      };

            
      window.analytics.track('Checkout Started', eventData);
    },

    /**
     * Track Product Viewed
     */
    trackProductViewed(product) {
      if (typeof window.analytics === 'undefined') return;
      
      const eventData = {
        product_id: String(product.id || ''),
        sku: product.sku || product.selected_or_first_available_variant?.sku || '',
        name: product.title || '',
        price: (product.price || 0) / 100,
        category: product.type || 'Supplements',
        brand: 'Mindvalley States',
        image_url: product.featured_image || '',
        url: product.url || window.location.href
      };

            
      window.analytics.track('Product Viewed', eventData);
    },

    /**
     * Track Product List Viewed
     */
    trackProductListViewed(listId, category, products) {
      if (typeof window.analytics === 'undefined') return;
      
      const productList = products.map(product => ({
        product_id: String(product.id || ''),
        sku: product.sku || '',
        name: product.title || '',
        price: (product.price || 0) / 100,
        category: product.type || 'Supplements',
        brand: 'Mindvalley States',
        image_url: product.featured_image || '',
        url: product.url || '',
        position: product.position || 0
      }));

      const eventData = {
        list_id: listId,
        category: category,
        products: productList
      };

            
      window.analytics.track('Product List Viewed', eventData);
    },

    /**
     * Track Quiz Started
     */
    trackQuizStarted(quizId, quizName) {
      if (typeof window.analytics === 'undefined') return;
      
      const eventData = {
        quiz_id: quizId,
        quiz_name: quizName,
        timestamp: new Date().toISOString()
      };

            
      window.analytics.track('Quiz Started', eventData);
    },

    /**
     * Track Quiz Completed
     */
    trackQuizCompleted(quizId, quizName, result, recommendations, scores) {
      if (typeof window.analytics === 'undefined') return;
      
      const eventData = {
        quiz_id: quizId,
        quiz_name: quizName,
        result: result,
        recommended_products: recommendations,
        score_breakdown: scores,
        timestamp: new Date().toISOString()
      };

            
      window.analytics.track('Quiz Completed', eventData);
    }
  };

  // ==========================================
  // Cart Functionality
  // ==========================================
  
  const Cart = {
    init() {
      this.cartToggle = document.getElementById('cart-toggle');
      this.cartDrawer = document.getElementById('cart-drawer');
      this.cartOverlay = document.getElementById('cart-overlay');
      this.cartClose = document.getElementById('cart-close');
      this.cartItems = document.getElementById('cart-items');
      this.cartTotal = document.getElementById('cart-total');
      this.headerCartCount = document.querySelector('[data-cart-count]');

      this.bindEvents();
      this.refresh(); // Load cart on init
    },

    bindEvents() {
      // Open cart
      if (this.cartToggle) {
        this.cartToggle.addEventListener('click', () => this.open());
      }
      
      // Close cart
      if (this.cartClose) {
        this.cartClose.addEventListener('click', () => this.close());
      }
      if (this.cartOverlay) {
        this.cartOverlay.addEventListener('click', () => this.close());
      }

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Add to cart buttons - include product-btn and [data-add-to-cart]
      document.querySelectorAll('.add-to-cart-btn, .add-to-cart, .cross-sell-btn, .product-btn, [data-add-to-cart]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const form = btn.closest('form');
          if (form) {
            this.addToCart(form);
          } else {
            // Check for variant-id first (from products-grid), then productId
            const variantId = btn.dataset.variantId || btn.dataset.productId;
            const quantity = parseInt(btn.dataset.quantity) || 1;
            if (variantId) {
              this.addToCartById(variantId, quantity);
            }
          }
        });
      });

      // Quantity buttons in cart
      if (this.cartItems) {
        this.cartItems.addEventListener('click', (e) => {
          if (e.target.classList.contains('qty-increase')) {
            this.updateQuantity(e.target.dataset.key, 1);
          } else if (e.target.classList.contains('qty-decrease')) {
            this.updateQuantity(e.target.dataset.key, -1);
          } else if (e.target.classList.contains('cart-item-remove')) {
            this.removeItem(e.target.dataset.key);
          }
        });
      }

      // Mobile add to cart
      const mobileAddToCart = document.getElementById('mobile-add-to-cart');
      if (mobileAddToCart) {
        mobileAddToCart.addEventListener('click', () => {
          const form = document.querySelector('form[action="/cart/add"]');
          if (form) this.addToCart(form);
        });
      }
    },

    async open() {
      if (this.cartDrawer) this.cartDrawer.classList.add('active');
      if (this.cartOverlay) this.cartOverlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Track Cart Viewed
      try {
        const cart = await this.getCart();
        SegmentTracking.trackCartViewed(cart);
      } catch (error) {
        console.error('Error tracking cart viewed:', error);
      }
    },

    close() {
      if (this.cartDrawer) this.cartDrawer.classList.remove('active');
      if (this.cartOverlay) this.cartOverlay.classList.remove('active');
      document.body.style.overflow = '';
    },

    async addToCart(form) {
      const formData = new FormData(form);
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        const item = await response.json();
        
        if (item.id) {
          // Track Product Added
          const quantity = parseInt(formData.get('quantity')) || 1;
          SegmentTracking.trackProductAdded(item, quantity);
          
          await this.refresh();
          this.open();
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    },

    async addToCartById(variantId, quantity = 1, source = null) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });
        
        const item = await response.json();
        
        if (item.id) {
          // Track Product Added
          SegmentTracking.trackProductAdded(item, quantity, source);
          
          await this.refresh();
          this.open();
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    },

    async updateQuantity(key, delta) {
      try {
        const cart = await this.getCart();
        const item = cart.items.find(i => i.key === key);
        
        if (item) {
          const newQuantity = item.quantity + delta;
          
          if (newQuantity <= 0) {
            await this.removeItem(key);
          } else {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: key,
                quantity: newQuantity
              })
            });
            
            await this.refresh();
          }
        }
      } catch (error) {
        console.error('Error updating quantity:', error);
      }
    },

    async removeItem(key) {
      try {
        // Get item info before removing for tracking
        const cart = await this.getCart();
        const item = cart.items.find(i => i.key === key);
        
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity: 0
          })
        });
        
        // Track Product Removed
        if (item) {
          SegmentTracking.trackProductRemoved(item, item.quantity);
        }
        
        await this.refresh();
      } catch (error) {
        console.error('Error removing item:', error);
      }
    },

    async getCart() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    async refresh() {
      const cart = await this.getCart();
      this.updateUI(cart);
    },

    updateUI(cart) {
      // Update cart count in header
      if (this.headerCartCount) {
        this.headerCartCount.textContent = cart.item_count;
      }
      // Also update any elements with data-cart-count
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Update cart total
      if (this.cartTotal) {
        this.cartTotal.textContent = formatMoney(cart.total_price);
      }

      // Update cart items
      if (this.cartItems) {
        if (cart.item_count === 0) {
          this.cartItems.innerHTML = `
            <div class="cart-empty">
              <p>Your cart is empty</p>
              <a href="/collections/all" class="btn-continue-shopping">Continue Shopping</a>
            </div>
          `;
        } else {
          this.cartItems.innerHTML = cart.items.map(item => `
            <div class="cart-item" data-line-item-key="${item.key}">
              <div class="cart-item-image">
                ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_128x128$1')}" alt="${item.title}" loading="lazy">` : ''}
              </div>
              <div class="cart-item-details">
                <h4>${item.product_title}</h4>
                ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="variant">${item.variant_title}</div>` : ''}
                <div class="price">${formatMoney(item.price)}</div>
                <div class="quantity-controls">
                  <button class="qty-decrease" data-key="${item.key}" aria-label="Decrease quantity">-</button>
                  <span>${item.quantity}</span>
                  <button class="qty-increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <button class="cart-item-remove" data-key="${item.key}" aria-label="Remove item">&times;</button>
            </div>
          `).join('');
        }
      }
    }
  };

  // ==========================================
  // Product Gallery
  // ==========================================
  
  const ProductGallery = {
    init() {
      const thumbnails = document.querySelectorAll('.thumbnail');
      const mainImage = document.getElementById('gallery-main-img');

      if (!thumbnails.length || !mainImage) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          // Update main image
          const imageUrl = thumb.dataset.imageUrl;
          mainImage.src = imageUrl;

          // Update active state
          thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        });
      });
    }
  };

  // ==========================================
  // Quantity Selector
  // ==========================================
  
  const QuantitySelector = {
    init() {
      document.querySelectorAll('.qty-selector').forEach(selector => {
        const decreaseBtn = selector.querySelector('[data-action="decrease"], .qty-decrease');
        const increaseBtn = selector.querySelector('[data-action="increase"], .qty-increase');
        const input = selector.querySelector('.qty-value');

        if (!decreaseBtn || !increaseBtn || !input) return;

        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          if (currentValue > 1) {
            input.value = currentValue - 1;
            this.updatePrice();
          }
        });

        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const max = parseInt(input.max) || 10;
          if (currentValue < max) {
            input.value = currentValue + 1;
            this.updatePrice();
          }
        });
      });
    },

    updatePrice() {
      // Update add to cart button price if needed
      const priceSpan = document.querySelector('.add-to-cart-price');
      const qtyInput = document.querySelector('.qty-value');
      
      if (priceSpan && qtyInput && window.productPrice) {
        const qty = parseInt(qtyInput.value) || 1;
        const total = window.productPrice * qty;
        priceSpan.textContent = formatMoney(total);
      }
    }
  };

  // ==========================================
  // Pricing Options
  // ==========================================
  
  const PricingOptions = {
    init() {
      const options = document.querySelectorAll('.pricing-option');
      const sellingPlanInput = document.getElementById('selling-plan-input');

      if (!options.length) return;

      options.forEach(option => {
        option.addEventListener('click', () => {
          // Update selection
          options.forEach(o => o.classList.remove('selected'));
          option.classList.add('selected');

          // Update hidden input
          if (sellingPlanInput) {
            sellingPlanInput.value = option.dataset.sellingPlanId || '';
          }
        });
      });
    }
  };

  // ==========================================
  // FAQ Accordion
  // ==========================================
  
  const FAQ = {
    init() {
      const faqItems = document.querySelectorAll('.faq-item');

      faqItems.forEach(item => {
        const question = item.querySelector('.faq-question');
        
        if (question) {
          question.addEventListener('click', () => {
            const isOpen = item.classList.contains('open');
            
            // Close all other items
            faqItems.forEach(i => i.classList.remove('open'));
            
            // Toggle current item
            if (!isOpen) {
              item.classList.add('open');
            }
          });

          // Keyboard accessibility
          question.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              question.click();
            }
          });
        }
      });
    }
  };

  // ==========================================
  // Testimonials Filter
  // ==========================================
  
  const TestimonialsFilter = {
    init() {
      const tabs = document.querySelectorAll('.filter-tab');
      const cards = document.querySelectorAll('.testimonial-card');

      if (!tabs.length) return;

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const filter = tab.dataset.filter;

          // Update active tab
          tabs.forEach(t => t.classList.remove('active'));
          tab.classList.add('active');

          // Filter cards
          cards.forEach(card => {
            if (filter === 'all' || card.dataset.product === filter) {
              card.style.display = '';
            } else {
              card.style.display = 'none';
            }
          });
        });
      });
    }
  };

  // ==========================================
  // Quiz
  // ==========================================
  
  const Quiz = {
    currentQuestion: 0,
    answers: [],

    init() {
      this.intro = document.getElementById('quiz-intro');
      this.questions = document.getElementById('quiz-questions');
      this.results = document.getElementById('quiz-results');
      this.startBtn = document.getElementById('quiz-start');
      this.backBtn = document.getElementById('quiz-back');
      this.nextBtn = document.getElementById('quiz-next');
      this.restartBtn = document.getElementById('quiz-restart');
      this.questionScreens = document.querySelectorAll('.quiz-question-screen');

      if (!this.intro) return;

      this.totalQuestions = this.questionScreens.length;
      this.bindEvents();
    },

    bindEvents() {
      if (this.startBtn) {
        this.startBtn.addEventListener('click', () => this.start());
      }

      if (this.backBtn) {
        this.backBtn.addEventListener('click', () => this.back());
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }

      if (this.restartBtn) {
        this.restartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.restart();
        });
      }

      // Option selection
      document.querySelectorAll('.quiz-option').forEach(option => {
        option.addEventListener('click', () => {
          const screen = option.closest('.quiz-question-screen');
          const isMultiSelect = screen.querySelector('.quiz-options').classList.contains('multi-select');

          if (isMultiSelect) {
            option.classList.toggle('selected');
          } else {
            screen.querySelectorAll('.quiz-option').forEach(o => o.classList.remove('selected'));
            option.classList.add('selected');
          }
        });
      });
    },

    start() {
      this.intro.style.display = 'none';
      this.questions.style.display = 'block';
      this.showQuestion(0);
    },

    showQuestion(index) {
      this.currentQuestion = index;
      
      this.questionScreens.forEach((screen, i) => {
        screen.style.display = i === index ? 'block' : 'none';
      });

      // Update back button visibility
      if (this.backBtn) {
        this.backBtn.style.visibility = index === 0 ? 'hidden' : 'visible';
      }

      // Update next button text
      if (this.nextBtn) {
        this.nextBtn.textContent = index === this.totalQuestions - 1 ? 'See Results' : 'Next';
      }
    },

    back() {
      if (this.currentQuestion > 0) {
        this.showQuestion(this.currentQuestion - 1);
      }
    },

    next() {
      // Save current answer
      const currentScreen = this.questionScreens[this.currentQuestion];
      const selected = currentScreen.querySelectorAll('.quiz-option.selected');
      
      this.answers[this.currentQuestion] = Array.from(selected).map(s => s.dataset.value);

      if (this.currentQuestion < this.totalQuestions - 1) {
        this.showQuestion(this.currentQuestion + 1);
      } else {
        this.showResults();
      }
    },

    showResults() {
      this.questions.style.display = 'none';
      this.results.style.display = 'block';
      
      // Generate recommendations based on answers
      const recommendations = this.generateRecommendations();
      const resultsContent = document.getElementById('quiz-results-content');
      
      if (resultsContent) {
        resultsContent.innerHTML = recommendations;
      }
    },

    generateRecommendations() {
      // Simple recommendation logic based on answers
      const allAnswers = this.answers.flat();
      const recommendations = [];

      if (allAnswers.includes('Better Focus') || allAnswers.includes('Morning') || allAnswers.includes('Brain Fog')) {
        recommendations.push({
          name: 'FOCUS',
          desc: 'Perfect for enhanced concentration and mental clarity',
          color: '#3b82f6'
        });
      }

      if (allAnswers.includes('Stress Relief') || allAnswers.includes('Stress') || allAnswers.includes('All Day')) {
        recommendations.push({
          name: 'ZEN',
          desc: 'Great for calm focus without drowsiness',
          color: '#10b981'
        });
      }

      if (allAnswers.includes('Mood Boost') || allAnswers.includes('Social Events') || allAnswers.includes('Social Anxiety')) {
        recommendations.push({
          name: 'BLISS',
          desc: 'Ideal for mood elevation and social confidence',
          color: '#f59e0b'
        });
      }

      if (allAnswers.includes('Better Sleep') || allAnswers.includes('Night') || allAnswers.includes('Poor Sleep')) {
        recommendations.push({
          name: 'DREAM',
          desc: 'Perfect for deep, restorative sleep',
          color: '#6366f1'
        });
      }

      if (allAnswers.includes('Enhanced Intimacy')) {
        recommendations.push({
          name: 'GAZM',
          desc: 'Great for deeper connection and intimacy',
          color: '#e11d48'
        });
      }

      // Default recommendation if nothing matched
      if (recommendations.length === 0) {
        recommendations.push({
          name: 'Box Set',
          desc: 'Try all 5 States to discover what works best for you',
          color: '#7a12d4'
        });
      }

      return `
        <div class="quiz-recommendations">
          ${recommendations.map(rec => `
            <div class="quiz-recommendation" style="border-left: 4px solid ${rec.color}">
              <h3>${rec.name}</h3>
              <p>${rec.desc}</p>
            </div>
          `).join('')}
          <a href="/collections/all" class="btn-primary" style="margin-top: 2rem;">
            Shop Recommended Products
          </a>
        </div>
      `;
    },

    restart() {
      this.currentQuestion = 0;
      this.answers = [];
      
      // Clear selections
      document.querySelectorAll('.quiz-option').forEach(o => o.classList.remove('selected'));
      
      this.results.style.display = 'none';
      this.intro.style.display = 'block';
    }
  };

  // ==========================================
  // Mobile Menu
  // ==========================================
  
  const MobileMenu = {
    init() {
      this.toggle = document.getElementById('mobile-menu-toggle');
      this.nav = document.getElementById('mobile-nav');

      if (!this.toggle || !this.nav) return;

      this.toggle.addEventListener('click', () => {
        this.nav.classList.toggle('active');
        this.toggle.classList.toggle('active');
      });
    }
  };

  // ==========================================
  // Announcement Bar
  // ==========================================
  
  const AnnouncementBar = {
    init() {
      const closeBtn = document.querySelector('.announcement-close');
      const bar = document.querySelector('.announcement-bar');

      if (!closeBtn || !bar) return;

      closeBtn.addEventListener('click', () => {
        bar.style.display = 'none';
        sessionStorage.setItem('announcementClosed', 'true');
      });

      // Check if already closed
      if (sessionStorage.getItem('announcementClosed') === 'true') {
        bar.style.display = 'none';
      }
    }
  };

  // ==========================================
  // Video Player
  // ==========================================
  
  const VideoPlayer = {
    init() {
      const wrappers = document.querySelectorAll('.video-wrapper');

      wrappers.forEach(wrapper => {
        const playBtn = wrapper.querySelector('.play-button');
        if (!playBtn) return;

        playBtn.addEventListener('click', () => {
          const videoId = wrapper.dataset.videoId;
          const videoType = wrapper.dataset.videoType || 'youtube';

          if (videoType === 'youtube' && videoId) {
            wrapper.innerHTML = `
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/${videoId}?autoplay=1" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
              ></iframe>
            `;
          } else if (videoType === 'vimeo' && videoId) {
            wrapper.innerHTML = `
              <iframe 
                width="100%" 
                height="100%" 
                src="https://player.vimeo.com/video/${videoId}?autoplay=1" 
                frameborder="0" 
                allow="autoplay; fullscreen" 
                allowfullscreen
              ></iframe>
            `;
          }
        });
      });
    }
  };

  // ==========================================
  // Smooth Scroll
  // ==========================================
  
  const SmoothScroll = {
    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
          const href = this.getAttribute('href');
          if (href === '#') return;

          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      });
    }
  };

  // ==========================================
  // Quiz Modal - 10 Question Assessment
  // ==========================================
  
  // Quiz state
  let quiz10Answers = {};
  let currentQuizQuestion = 0;
  const totalQuizQuestions = 10;

  // Product data for quiz results
  const productData10 = {
    focus: {
      name: 'FOCUS',
      desc: 'Caffeine-free energy for peak focus. 4-6 hours of deep work without jitters or crash.',
      img: 'https://www.mindvalleystates.com/cdn/shop/files/Focus_4K_1.png?v=1760015485&width=400',
      price: window.quizProductPrices?.focus || 59,
      timeIcon: '🌅',
      timeText: 'Best taken in the morning before deep work sessions'
    },
    zen: {
      name: 'ZEN',
      desc: 'De-stress and reset in minutes. Calm resilience under pressure without drowsiness.',
      img: 'https://www.mindvalleystates.com/cdn/shop/files/Zen_4K_1.png?v=1760015438&width=400',
      price: window.quizProductPrices?.zen || 59,
      timeIcon: '☀️',
      timeText: 'Take before stressful situations or to calm racing thoughts'
    },
    bliss: {
      name: 'BLISS',
      desc: 'Happiness powered by science. Elevated mood and social openness.',
      img: 'https://www.mindvalleystates.com/cdn/shop/files/Bliss_4K_1.png?v=1760015460&width=400',
      price: window.quizProductPrices?.bliss || 59,
      timeIcon: '🌆',
      timeText: 'Afternoon or 30-45 min before social events'
    },
    dream: {
      name: 'DREAM',
      desc: 'Drift into restful, restorative sleep. Wake refreshed with vivid dreams.',
      img: 'https://www.mindvalleystates.com/cdn/shop/files/Dream_4K_1.png?v=1760015386&width=400',
      price: window.quizProductPrices?.dream || 59,
      timeIcon: '🌙',
      timeText: 'Take 30-60 minutes before bed'
    },
    gazm: {
      name: 'GAZM',
      desc: 'Ignite connection and deepen presence. Enhanced blood flow and hormone balance.',
      img: 'https://www.mindvalleystates.com/cdn/shop/files/Gazm_4K_1.png?v=1760015415&width=400',
      price: window.quizProductPrices?.gazm || 59,
      timeIcon: '🌆',
      timeText: 'Take 30-60 minutes before intimate moments'
    }
  };

  // Quiz questions data
  const quizQuestions10 = [
    {
      q: "What's your biggest daily challenge?",
      options: [
        { value: 'focus', icon: '🎯', title: 'Staying focused and productive', desc: 'I get distracted easily' },
        { value: 'stress', icon: '😰', title: 'Managing stress and anxiety', desc: 'I often feel overwhelmed' },
        { value: 'mood', icon: '💔', title: 'Low mood or motivation', desc: 'I struggle to feel positive' },
        { value: 'sleep', icon: '😴', title: 'Poor sleep quality', desc: 'I don\'t wake up refreshed' },
        { value: 'energy', icon: '⚡', title: 'Low energy throughout the day', desc: 'I rely on coffee but crash' }
      ]
    },
    {
      q: "When do you need the most support?",
      options: [
        { value: 'morning', icon: '🌅', title: 'Morning (6am - 12pm)', desc: 'I need help getting started' },
        { value: 'afternoon', icon: '☀️', title: 'Afternoon (12pm - 6pm)', desc: 'I hit a wall mid-day' },
        { value: 'evening', icon: '🌆', title: 'Evening (6pm - 10pm)', desc: 'I need help winding down' },
        { value: 'night', icon: '🌙', title: 'Night (10pm+)', desc: 'I struggle to fall asleep' }
      ]
    },
    {
      q: "What's your relationship with caffeine?",
      options: [
        { value: 'heavy', icon: '☕', title: 'Heavy coffee drinker (3+ cups)', desc: 'I get jitters and crashes' },
        { value: 'moderate', icon: '☕', title: 'Moderate (1-2 cups/day)', desc: 'Works okay, want cleaner option' },
        { value: 'sensitive', icon: '😟', title: 'Sensitive to caffeine', desc: 'Even a little makes me anxious' },
        { value: 'none', icon: '🚫', title: 'I avoid caffeine', desc: 'Prefer caffeine-free solutions' }
      ]
    },
    {
      q: "What type of work do you do?",
      options: [
        { value: 'knowledge', icon: '💻', title: 'Knowledge work / Creative', desc: 'Writing, coding, design, analysis' },
        { value: 'leadership', icon: '👔', title: 'Leadership / Management', desc: 'Meetings, decisions, teams' },
        { value: 'entrepreneur', icon: '🚀', title: 'Entrepreneur / Founder', desc: 'Multiple hats, high stakes' },
        { value: 'other', icon: '🎯', title: 'Other / Varied', desc: 'Mix of different activities' }
      ]
    },
    {
      q: "How would you describe your stress level?",
      options: [
        { value: 'high', icon: '🔥', title: 'High - constantly stressed', desc: 'I feel overwhelmed most of the time' },
        { value: 'moderate', icon: '⚖️', title: 'Moderate - manageable', desc: 'Some stressful periods but okay' },
        { value: 'situational', icon: '💡', title: 'Situational', desc: 'Only before big events/deadlines' },
        { value: 'low', icon: '😊', title: 'Low - pretty calm', desc: 'I handle stress well' }
      ]
    },
    {
      q: "How's your sleep quality?",
      options: [
        { value: 'poor', icon: '😭', title: 'Poor - I rarely feel rested', desc: 'Trouble falling/staying asleep' },
        { value: 'inconsistent', icon: '⚖️', title: 'Inconsistent', desc: 'Some nights good, others terrible' },
        { value: 'okay', icon: '😐', title: 'Okay - could be better', desc: 'I sleep but don\'t feel refreshed' },
        { value: 'good', icon: '😊', title: 'Good - I sleep well', desc: 'No major sleep issues' }
      ]
    },
    {
      q: "Do you have presentations or public speaking?",
      options: [
        { value: 'frequent', icon: '🎬', title: 'Yes, frequently', desc: 'Multiple times per week' },
        { value: 'sometimes', icon: '📝', title: 'Sometimes', desc: 'A few times per month' },
        { value: 'rarely', icon: '💬', title: 'Rarely', desc: 'Occasional big moments' },
        { value: 'never', icon: '💻', title: 'Never', desc: 'My work doesn\'t require it' }
      ]
    },
    {
      q: "Is intimate connection important to you?",
      options: [
        { value: 'priority', icon: '❤️', title: 'Yes, it\'s a priority', desc: 'I want to enhance connection' },
        { value: 'interested', icon: '💝', title: 'I\'m interested', desc: 'I\'d like to improve this area' },
        { value: 'not-now', icon: '💧', title: 'Not a focus right now', desc: 'Other priorities at the moment' }
      ]
    },
    {
      q: "What's your experience with supplements?",
      options: [
        { value: 'new', icon: '🌱', title: 'New to this', desc: 'Haven\'t tried nootropics before' },
        { value: 'some', icon: '📚', title: 'Some experience', desc: 'Tried basic supplements' },
        { value: 'experienced', icon: '🔬', title: 'Experienced', desc: 'Experimented with various stacks' },
        { value: 'biohacker', icon: '🧬', title: 'Serious biohacker', desc: 'Deep into optimization' }
      ]
    },
    {
      q: "What would make the biggest difference?",
      subtext: "Select all that apply",
      multi: true,
      options: [
        { value: 'productivity', icon: '🚀', title: 'More productivity', desc: 'Get more done in less time' },
        { value: 'calm', icon: '😊', title: 'More calm, less anxiety', desc: 'Feel centered under pressure' },
        { value: 'happiness', icon: '☀️', title: 'More happiness', desc: 'Feel genuinely good more often' },
        { value: 'rest', icon: '💤', title: 'Better sleep', desc: 'Wake up feeling refreshed' },
        { value: 'connection', icon: '💕', title: 'Deeper connection', desc: 'More presence in relationships' }
      ]
    }
  ];

  // Quiz functions
  window.openQuiz = function() {
    const overlay = document.getElementById('quiz-overlay');
    if (overlay) {
      overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  };

  window.closeQuiz = function(e) {
    if (e && e.target === document.getElementById('quiz-overlay')) {
      closeQuizDirect();
    }
  };

  window.closeQuizDirect = function() {
    const overlay = document.getElementById('quiz-overlay');
    if (overlay) {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
      resetQuiz10();
    }
  };

  window.startQuiz10 = function() {
    const intro = document.getElementById('quiz-intro-screen');
    const container = document.getElementById('quiz-questions-container');
    
    if (!intro || !container) return;
    
    intro.style.display = 'none';
    container.style.display = 'block';
    currentQuizQuestion = 1;
    quiz10Answers = {};
    
    // Track Quiz Started
    SegmentTracking.trackQuizStarted('state-finder', 'Find Your Optimal State');
    
    // Render questions
    renderQuizQuestions();
    showQuestion(1);
    updateQuizProgress();
  };

  function renderQuizQuestions() {
    const container = document.getElementById('quiz-questions-container');
    if (!container) return;
    
    container.innerHTML = quizQuestions10.map((qData, idx) => {
      const qNum = idx + 1;
      const isMulti = qData.multi || false;
      return `
        <div class="quiz-content" id="quiz-q${qNum}" style="display:none">
          <div style="font-size:12px;color:#7a12d4;font-family:'Sharp Grotesk Cyr Semibold 20',sans-serif;margin-bottom:8px;">Question ${qNum} of 10</div>
          <div class="quiz-question">${qData.q}</div>
          ${qData.subtext ? `<p style="color:#71767f;font-size:13px;margin-top:-12px;margin-bottom:16px;">${qData.subtext}</p>` : ''}
          <div class="quiz-options ${isMulti ? 'multi-select' : ''}" id="q${qNum}-options">
            ${qData.options.map(opt => `
              <div class="quiz-option" data-value="${opt.value}" onclick="${isMulti ? 'toggleMulti10' : 'selectAnswer10'}(${qNum}, this)">
                <span class="quiz-option-icon">${opt.icon}</span>
                <div><strong>${opt.title}</strong><br><span style="font-size:13px;color:#71767f">${opt.desc}</span></div>
              </div>
            `).join('')}
          </div>
          ${qNum === 10 ? '<button class="btn-primary" id="show-results-btn" onclick="showQuizResults10()" style="width:100%;margin-top:20px;opacity:0.5;pointer-events:none;">See My Results</button>' : ''}
        </div>
      `;
    }).join('');
  }

  function showQuestion(qNum) {
    for (let i = 1; i <= 10; i++) {
      const q = document.getElementById('quiz-q' + i);
      if (q) q.style.display = i === qNum ? 'block' : 'none';
    }
  }

  window.selectAnswer10 = function(q, el) {
    const options = el.parentElement.querySelectorAll('.quiz-option');
    options.forEach(opt => opt.classList.remove('selected'));
    el.classList.add('selected');
    quiz10Answers[q] = el.dataset.value;
    
    // Auto-advance
    setTimeout(() => {
      if (q < 10) {
        showQuestion(q + 1);
        currentQuizQuestion = q + 1;
        updateQuizProgress();
      }
    }, 300);
  };

  window.toggleMulti10 = function(qNum, el) {
    el.classList.toggle('selected');
    const selected = document.querySelectorAll(`#quiz-q${qNum} .quiz-option.selected`);
    quiz10Answers[qNum] = Array.from(selected).map(opt => opt.dataset.value);
    
    const btn = document.getElementById('show-results-btn');
    if (btn) {
      if (selected.length > 0) {
        btn.style.opacity = '1';
        btn.style.pointerEvents = 'auto';
      } else {
        btn.style.opacity = '0.5';
        btn.style.pointerEvents = 'none';
      }
    }
  };

  function updateQuizProgress() {
    const bar = document.getElementById('quiz-progress-bar');
    const text = document.getElementById('quiz-progress-text');
    if (bar) bar.style.width = (currentQuizQuestion / totalQuizQuestions * 100) + '%';
    if (text) text.textContent = currentQuizQuestion + '/10';
  }

  function calculateQuizResults() {
    let scores = { focus: 0, zen: 0, bliss: 0, dream: 0, gazm: 0 };

    // Scoring logic from mockup
    if (quiz10Answers[1] === 'focus' || quiz10Answers[1] === 'energy') scores.focus += 3;
    if (quiz10Answers[1] === 'stress') scores.zen += 3;
    if (quiz10Answers[1] === 'mood') scores.bliss += 3;
    if (quiz10Answers[1] === 'sleep') scores.dream += 3;

    if (quiz10Answers[2] === 'morning') scores.focus += 2;
    if (quiz10Answers[2] === 'afternoon') { scores.zen += 1; scores.bliss += 1; }
    if (quiz10Answers[2] === 'evening') { scores.bliss += 1; scores.gazm += 1; }
    if (quiz10Answers[2] === 'night') scores.dream += 2;

    if (quiz10Answers[3] === 'heavy' || quiz10Answers[3] === 'moderate') scores.focus += 2;
    if (quiz10Answers[3] === 'sensitive' || quiz10Answers[3] === 'none') { scores.zen += 1; scores.bliss += 1; }

    if (quiz10Answers[4] === 'knowledge') scores.focus += 2;
    if (quiz10Answers[4] === 'leadership' || quiz10Answers[4] === 'entrepreneur') { scores.focus += 1; scores.zen += 1; }

    if (quiz10Answers[5] === 'high') scores.zen += 3;
    if (quiz10Answers[5] === 'moderate') scores.zen += 1;
    if (quiz10Answers[5] === 'situational') scores.zen += 2;

    if (quiz10Answers[6] === 'poor') scores.dream += 3;
    if (quiz10Answers[6] === 'inconsistent') scores.dream += 2;
    if (quiz10Answers[6] === 'okay') scores.dream += 1;

    if (quiz10Answers[7] === 'frequent' || quiz10Answers[7] === 'sometimes') { scores.focus += 1; scores.zen += 2; }

    if (quiz10Answers[8] === 'priority') scores.gazm += 3;
    if (quiz10Answers[8] === 'interested') scores.gazm += 2;

    if (quiz10Answers[10] && quiz10Answers[10].length > 0) {
      quiz10Answers[10].forEach(val => {
        if (val === 'productivity') scores.focus += 2;
        if (val === 'calm') scores.zen += 2;
        if (val === 'happiness') scores.bliss += 2;
        if (val === 'rest') scores.dream += 2;
        if (val === 'connection') scores.gazm += 2;
      });
    }

    return Object.entries(scores).sort((a, b) => b[1] - a[1]);
  }

  window.showQuizResults10 = function() {
    const sorted = calculateQuizResults();
    const primaryKey = sorted[0][0];
    const secondary = sorted.slice(1, 3).map(s => s[0]);
    const prod = productData10[primaryKey];

    // Hide questions, show results
    document.getElementById('quiz-questions-container').style.display = 'none';
    const result = document.getElementById('quiz-result');
    if (result) {
      result.style.display = 'block';
      
      // Populate primary
      document.getElementById('result-img').src = prod.img;
      document.getElementById('result-name').textContent = prod.name;
      document.getElementById('result-desc').textContent = prod.desc;
      document.getElementById('result-price').textContent = prod.price;
      document.getElementById('timing-icon').innerHTML = prod.timeIcon;
      document.getElementById('timing-text').textContent = prod.timeText;
      
      // Populate secondary
      const secRecs = document.getElementById('secondary-recs');
      if (secRecs) {
        const secHtml = secondary.map(key => {
          const p = productData10[key];
          return `
            <div style="display:flex;gap:12px;align-items:center;background:#f9fafb;padding:12px;border-radius:8px;margin-bottom:8px;">
              <img src="${p.img}" style="width:50px;height:50px;object-fit:contain;">
              <div style="flex:1;">
                <strong style="font-size:14px;">${p.name}</strong>
                <span style="display:block;font-size:12px;color:#71767f;">${p.timeText}</span>
              </div>
            </div>
          `;
        }).join('');
        secRecs.innerHTML = '<p style="font-size:13px;color:#71767f;margin-bottom:8px;">Also recommended:</p>' + secHtml;
      }

      // Stack recommendation logic
      const stackRecEl = document.getElementById('stack-rec');
      if (stackRecEl) {
        let stackTitle = 'Your Power Stack';
        let stackProducts = [sorted[0][0], sorted[1][0]];
        let stackProtocol = '';

        // Determine stack based on quiz answers
        if (primaryKey === 'focus' && (quiz10Answers[5] === 'high' || quiz10Answers[5] === 'situational' || quiz10Answers[7] === 'frequent')) {
          stackProducts = ['focus', 'zen'];
          stackTitle = 'The Presentation Stack';
          stackProtocol = 'Take FOCUS in the morning. Add ZEN 30 min before high-stakes meetings for calm confidence.';
        } else if (primaryKey === 'bliss' && quiz10Answers[8] !== 'not-now') {
          stackProducts = ['bliss', 'gazm'];
          stackTitle = 'The Connection Stack';
          stackProtocol = 'Take BLISS in the afternoon. Add GAZM 30-60 min before intimate time.';
        } else if (quiz10Answers[6] === 'poor' || quiz10Answers[6] === 'inconsistent') {
          stackProducts = ['zen', 'dream'];
          stackTitle = 'The Recovery Stack';
          stackProtocol = 'Take ZEN in the evening to wind down. Take DREAM 30-60 min before bed.';
        } else {
          stackProtocol = `Use ${productData10[stackProducts[0]].name} as your primary, add ${productData10[stackProducts[1]].name} for complementary benefits.`;
        }

        document.getElementById('stack-title').textContent = stackTitle;
        document.getElementById('stack-products').innerHTML = `
          <img src="${productData10[stackProducts[0]].img}" style="width:60px;height:60px;object-fit:contain;">
          <span style="font-size:20px;opacity:0.5;">+</span>
          <img src="${productData10[stackProducts[1]].img}" style="width:60px;height:60px;object-fit:contain;">
        `;
        document.getElementById('stack-protocol').textContent = stackProtocol;
      }
      
      // Update progress
      const bar = document.getElementById('quiz-progress-bar');
      const text = document.getElementById('quiz-progress-text');
      if (bar) bar.style.width = '100%';
      if (text) text.textContent = 'Complete';
      
      // Track Quiz Completed
      const recommendedProducts = [primaryKey, ...secondary];
      const scoreBreakdown = {};
      sorted.forEach(([key, score]) => {
        scoreBreakdown[key] = score;
      });
      
      SegmentTracking.trackQuizCompleted(
        'state-finder',
        'Find Your Optimal State',
        primaryKey,
        recommendedProducts,
        scoreBreakdown
      );
    }
  };

  window.addToCartFromQuiz = function() {
    const sorted = calculateQuizResults();
    const primary = sorted[0][0];
    const prod = productData10[primary];
    
    if (window.quizProductIds && window.quizProductIds[primary]) {
      Cart.addToCartById(window.quizProductIds[primary], 1, 'quiz');
    } else {
      alert(`Added ${prod.name} to cart!`);
    }
  };

  window.addToCart = function(handle, title, price) {
    if (window.quizProductIds && window.quizProductIds[handle]) {
      Cart.addToCartById(window.quizProductIds[handle], 1, 'quiz');
    } else {
      // Fallback for box set if handle doesn't match (e.g. if handle is just 'box-set' but map has it)
      // The map has "box-set" so it should work.
      alert(`Added ${title} to cart!`);
    }
  };

  window.resetQuiz10 = function() {
    quiz10Answers = {};
    currentQuizQuestion = 0;
    
    const intro = document.getElementById('quiz-intro-screen');
    const container = document.getElementById('quiz-questions-container');
    const result = document.getElementById('quiz-result');
    
    if (intro) intro.style.display = 'block';
    if (container) {
      container.style.display = 'none';
      container.innerHTML = ''; // Clear any previously rendered questions
    }
    if (result) result.style.display = 'none';
    
    document.querySelectorAll('.quiz-option').forEach(o => o.classList.remove('selected'));
    
    const bar = document.getElementById('quiz-progress-bar');
    const text = document.getElementById('quiz-progress-text');
    if (bar) bar.style.width = '0%';
    if (text) text.textContent = '0/10';
    
    const btn = document.getElementById('show-results-btn');
    if (btn) {
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
    }
  };

  // ==========================================
  // Initialize All Modules
  // ==========================================
  
  document.addEventListener('DOMContentLoaded', () => {
    Cart.init();
    ProductGallery.init();
    QuantitySelector.init();
    PricingOptions.init();
    FAQ.init();
    TestimonialsFilter.init();
    Quiz.init();
    MobileMenu.init();
    AnnouncementBar.init();
    VideoPlayer.init();
    SmoothScroll.init();
  });

  // Expose SegmentTracking globally for use in Liquid templates
  window.SegmentTracking = SegmentTracking;

  // ==========================================
  // Mindvalley OAuth Login Function
  // ==========================================
  
  /**
   * Redirects to Mindvalley OAuth login page
   * Can be called programmatically from buttons, links, or conditional logic
   */
  function gotoMindvalleyLogin() {
    window.location.href = 'https://login.mindvalley.com/authorize?response_type=code&client_id=aSVRkkinfpylyTeKHkLQKbPkaJw29OHg';
  }

  // Expose globally for use throughout the theme
  window.gotoMindvalleyLogin = gotoMindvalleyLogin;

  // ==========================================
  // Countdown Timer Web Component
  // ==========================================
  
  /**
   * Countdown Timer Custom Element
   * Renders a countdown timer that updates every second
   * and handles expiration behavior (hide section or show message)
   */
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();

      // Selectors
      this.selectors = {
        time: 'time',
        days: '[data-days]',
        hours: '[data-hours]',
        minutes: '[data-minutes]',
        seconds: '[data-seconds]',
        shopifySection: '.shopify-section',
      };

      // Settings
      this.settings = {
        hideSection: 'hide-section',
        showMessage: 'show-message',
      };

      // Get DOM elements
      this.shopifySection = this.closest(this.selectors.shopifySection);
      this.expirationBehavior = this.getAttribute('data-expiration-behavior');
      
      this.time = this.querySelector(this.selectors.time);
      this.days = this.querySelector(this.selectors.days);
      this.hours = this.querySelector(this.selectors.hours);
      this.minutes = this.querySelector(this.selectors.minutes);
      this.seconds = this.querySelector(this.selectors.seconds);

      // Get the current and expiration dates in Unix timestamp format (milliseconds)
      this.endDate = Date.parse(this.time.dateTime);
      this.daysInMs = 1000 * 60 * 60 * 24;
      this.hoursInMs = this.daysInMs / 24;
      this.minutesInMs = this.hoursInMs / 60;
      this.secondsInMs = this.minutesInMs / 60;

      this.shouldHideOnComplete = this.expirationBehavior === this.settings.hideSection;
      this.shouldShowMessage = this.expirationBehavior === this.settings.showMessage;

      this.update = this.update.bind(this);
    }

    connectedCallback() {
      if (isNaN(this.endDate)) {
        this.onComplete();
        return;
      }

      if (this.endDate <= Date.now()) {
        this.onComplete();
        return;
      }

      // Update the countdown every second
      this.interval = setInterval(this.update, 1000);
    }

    disconnectedCallback() {
      this.stopTimer();
    }

    convertTime(timeInMs) {
      const days = this.formatDigits(parseInt(timeInMs / this.daysInMs, 10));
      timeInMs -= days * this.daysInMs;

      const hours = this.formatDigits(parseInt(timeInMs / this.hoursInMs, 10));
      timeInMs -= hours * this.hoursInMs;

      const minutes = this.formatDigits(parseInt(timeInMs / this.minutesInMs, 10));
      timeInMs -= minutes * this.minutesInMs;

      const seconds = this.formatDigits(parseInt(timeInMs / this.secondsInMs, 10));

      return {
        days: days,
        hours: hours,
        minutes: minutes,
        seconds: seconds,
      };
    }

    // Make numbers less than 10 to appear with a leading zero like 01, 02, 03
    formatDigits(number) {
      if (number < 10) number = '0' + number;
      return number;
    }

    render(timer) {
      this.days.textContent = timer.days;
      this.hours.textContent = timer.hours;
      this.minutes.textContent = timer.minutes;
      this.seconds.textContent = timer.seconds;
    }

    stopTimer() {
      clearInterval(this.interval);
    }

    onComplete() {
      this.render({
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      });

      if (this.shouldHideOnComplete && this.shopifySection) {
        this.shopifySection.classList.add('hidden');
        this.shopifySection.style.display = 'none';
      }

      if (this.shouldShowMessage) {
        this.classList.add('show-message');
      }
    }

    // Function to update the countdown
    update() {
      const timeNow = new Date().getTime();
      const timeDiff = this.endDate - timeNow;

      if (timeDiff <= 0) {
        this.stopTimer();
        this.onComplete();
        return;
      }

      const timeRemaining = this.convertTime(timeDiff);
      this.render(timeRemaining);
    }
  }

  // Register the custom element
  if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', CountdownTimer);
  }

  // ==========================================
  // Scroll Reveal Animation System
  // ==========================================

  const ScrollReveal = {
    init() {
      // Add reveal classes to sections
      this.observeSections();
      this.observeElements();
      this.initHeroAnimation();
      this.initParallax();
    },

    observeSections() {
      const sections = document.querySelectorAll('section, .shopify-section');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            // Stagger children animations
            const children = entry.target.querySelectorAll('.reveal-item');
            children.forEach((child, index) => {
              child.style.transitionDelay = `${index * 0.1}s`;
              child.classList.add('revealed');
            });
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      sections.forEach(section => {
        section.classList.add('reveal-section');
        observer.observe(section);
      });
    },

    observeElements() {
      const elements = document.querySelectorAll(
        '.hero__title, .hero__subtitle, .hero__ctas, .hero__stats, ' +
        'h1, h2, h3, .product-card, .testimonial-card, .stat-item, ' +
        '.feature-item, .benefit-item'
      );

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      });

      elements.forEach((el, index) => {
        el.classList.add('animate-on-scroll');
        el.style.setProperty('--animation-order', index % 5);
        observer.observe(el);
      });
    },

    initHeroAnimation() {
      const hero = document.querySelector('.hero');
      if (!hero) return;

      // Animate hero elements on load
      setTimeout(() => {
        hero.classList.add('hero-animated');
      }, 100);

      // Parallax effect on scroll
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const scrolled = window.pageYOffset;
            const heroContent = hero.querySelector('.hero__content');
            const heroMedia = hero.querySelector('.hero__media');

            if (heroContent && scrolled < window.innerHeight) {
              heroContent.style.transform = `translateY(${scrolled * 0.3}px)`;
              heroContent.style.opacity = 1 - (scrolled / (window.innerHeight * 0.8));
            }
            if (heroMedia && scrolled < window.innerHeight) {
              heroMedia.style.transform = `translateY(${scrolled * 0.15}px) scale(${1 - scrolled * 0.0003})`;
            }
            ticking = false;
          });
          ticking = true;
        }
      });
    },

    initParallax() {
      const parallaxElements = document.querySelectorAll('[data-parallax]');

      window.addEventListener('scroll', () => {
        const scrolled = window.pageYOffset;

        parallaxElements.forEach(el => {
          const speed = el.dataset.parallax || 0.5;
          const yPos = -(scrolled * speed);
          el.style.transform = `translateY(${yPos}px)`;
        });
      });
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ScrollReveal.init());
  } else {
    ScrollReveal.init();
  }

})();


